/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { type Blueprint } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { AppRootContextProvider } from '@r-app/app-root-context-provider';
import { BlueprintSvgContextMenuWrapper } from './blueprint-svg-context-menu-wrapper';
import { ComponentController } from '@r-core/controller';
import { computeFittedViewBox } from './compute-fitted-viewbox';
import { computeFlatTrimBounds } from './compute-flat-trim-bounds';
import { CreateBlueprintSymbols } from '../../symbols';
import { DisplayMode } from '@r-core/controller/enums/display-mode.enum';
import { DisplayOverheadMode } from '../../display-mode/display-overhead-mode';
import { DisplayPanelMode } from '../../display-mode/display-panel-mode';
import { RenderBacksplashes } from '../../render/render-backsplashes';
import { RenderCornerTrims } from '../../render/render-corner-trims';
import { getAdjoiningCountertopSeams } from '../helpers/get-adjoining-countertop-seams';
import { getCleanEdgeAddableCandidates, getCleanEdgeLabels } from '../helpers/generate-clean-edge-annotations';
import { getRenderedContentBounds } from '@r-core/helpers/get-rendered-content-bounds';
import { OverheadIslandLetters } from './overhead-island-letters';
import { raceFrame } from '../helpers/race-frame';
import { RenderAdjoiningSeamMarker } from './render-adjoining-seam-marker';
import { RenderCleanEdgeAddCandidate } from './render-clean-edge-add-candidate';
import { RenderCleanEdgeLabel } from './render-clean-edge-label';
import { RenderJointCountertopRow } from './render-joint-countertop-row';
import { RenderSkuAnnotation } from '../../sku-annotations/render-sku-annotation';
import { usePageFontFactorOverride, useReportComputedFontFactor } from './page-font-factor-context';
import { useSkuAnnotation } from '../../sku-annotations/sku-annotation-context';
import { useSheetDimensionVisibility } from './use-sheet-dimension-visibility.hook';
import { useStabilizedInkExtent } from './use-stabilized-ink-extent.hook';

export type EmbeddedSvgViewportProps = {
	blueprint: Blueprint;
	islandId: string;
	panelId: string | null;
	displayMode: DisplayMode;
	pageIndex: number;
	pageType?: string;
	/** Overhead sheets only: draw ONLY these islands (e.g. a joint countertop group), not the whole kitchen. */
	overheadIslandIds?: Array<string> | null;
	/** Overhead Layout sheet only: force the two overall width/depth dimension lines off, regardless of the working view's own toggle -- GitHub #450. */
	hideOverheadDimensions?: boolean;
};

/**
 * Padding ratio applied around content when computing the viewBox.
 * A value of 0.15 means 15% extra space on each side.
 *
 * For wide panels the percentage-based padding eats real estate and forces the
 * content to scale down further than necessary. We cap the absolute padding at
 * `VIEWBOX_PADDING_MAX_INCHES` so wide panels keep most of the page width:
 *   30" panel  → 4.5" padding (15%, comfortable)
 *   50" panel  → 6"   padding (capped, ~12%)
 *   100" panel → 6"   padding (capped, ~6%)
 *   200" panel → 6"   padding (capped, ~3%)
 */
const VIEWBOX_PADDING_RATIO = 0.15;
const VIEWBOX_PADDING_MAX_INCHES = 6;

const computePadding = (dimension: number): number => {
	return Math.min(dimension * VIEWBOX_PADDING_RATIO, VIEWBOX_PADDING_MAX_INCHES);
};

type ContentBounds = {
	x: number;
	y: number;
	width: number;
	height: number;
};

/**
 * Computes an initial viewBox estimate from domain model dimensions.
 */
const computeModelBounds = (params: {
	controller: ComponentController;
	islandId: string;
	panelId: string | null;
	displayMode: DisplayMode;
	fontFactor: number;
}): ContentBounds | null => {
	const { controller, islandId, panelId, displayMode, fontFactor } = params;
	const { kitchen } = controller;

	if (!kitchen) return null;

	const isCornerTrim = displayMode === DisplayMode.CornerTrim
		|| displayMode === DisplayMode.CornerTrimManufacturing;

	// the trim cut list is kitchen-wide and its labels sit outside the pieces, so it needs its own bounds
	if (isCornerTrim) return computeFlatTrimBounds({ kitchen, fontFactor });

	const isBacksplash = displayMode === DisplayMode.Backsplash
		|| displayMode === DisplayMode.BacksplashManufacturing;

	if (isBacksplash) {
		// strips render as a vertical stack centred on x=0 from y=0 down (see RenderBacksplashes); the getBBox refine tightens this
		const backsplashes = kitchen.design.islands.children
			.flatMap((island) => island.getCountertop()?.backsplashes?.children ?? []);
		if (backsplashes.length === 0) return null;
		const maxWidth = Math.max(...backsplashes.map((b) => b.size?.width ?? 0));
		const totalHeight = backsplashes.reduce((sum, b) => sum + (b.size?.height ?? 4) + 4, 4);
		return { x: 0, y: totalHeight / 2, width: maxWidth, height: totalHeight };
	}

	const island = kitchen.design.islands.findById(islandId);
	if (!island) return null;

	const isOverhead = displayMode === DisplayMode.OverheadManufacturing
		|| displayMode === DisplayMode.Overhead;

	if (isOverhead) {
		const kitchenSize = kitchen.getSize(true);
		const { width, depth, origin } = kitchenSize;

		return {
			x: origin.x,
			y: origin.z,
			width,
			height: depth
		};
	}

	const isCountertop = panelId === null;

	if (isCountertop) {
		const { islandSize } = island;

		return {
			x: 0,
			y: 0,
			width: islandSize.width,
			height: islandSize.depth
		};
	}

	const panel = island.panels.findById(panelId);
	if (!panel) return null;

	const { islandSize } = island;
	const distanceAboveCountertop = panel.panelItemGroups.getMaxDistanceAboveCountertop();
	const leftOverhang = panel.getLeftPanel()?.countertopOverhang ?? 0;
	const rightOverhang = panel.getRightPanel()?.countertopOverhang ?? 0;

	const contentWidth = panel.isSidePanel
		? islandSize.depth
		: panel.size.width + leftOverhang + rightOverhang;
	const contentHeight = islandSize.height + distanceAboveCountertop;

	const xOffset = (rightOverhang - leftOverhang) / 2;
	const yOffset = distanceAboveCountertop / -2;

	return {
		x: xOffset,
		y: yOffset,
		width: contentWidth,
		height: contentHeight
	};
};

/**
 * Builds a viewBox string from content bounds with padding.
 */
const buildViewBox = (bounds: ContentBounds): string => {
	const { x, y, width, height } = bounds;
	const padX = computePadding(width);
	const padY = computePadding(height);
	const vbX = x - (width / 2) - padX;
	const vbY = y - (height / 2) - padY;
	const vbW = width + (padX * 2);
	const vbH = height + (padY * 2);

	return `${vbX} ${vbY} ${vbW} ${vbH}`;
};

const EmbeddedSvgViewportInner = (props: EmbeddedSvgViewportProps): React.ReactElement => {
	const { blueprint, islandId, panelId, displayMode, pageIndex, pageType, overheadIslandIds = null, hideOverheadDimensions = false } = props;

	// Read the main controller's font factor so blueprint view matches regular views
	const mainContext = useContext(AppContext);
	const baseFactor = mainContext?.controller?.options?.dimensionFontSizeFactor ?? null;

	const svgRef = useRef<SVGSVGElement>(null);
	const contentGroupRef = useRef<SVGGElement>(null);
	const [refinedViewBox, setRefinedViewBox] = useState<string | null>(null);
	const [containerPx, setContainerPx] = useState<{ w: number; h: number }>({ w: 800, h: 400 });

	// Per-page override (from BlueprintPageFontControl). When set, it replaces
	// the controller-derived factor entirely so the user has direct control.
	const pageFontFactorOverride = usePageFontFactorOverride();
	const reportComputedFactor = useReportComputedFontFactor();

	// Start from the main controller's font size, then compensate for zoom.
	const computedFontFactor = (baseFactor + 0.06);
	const fontFactor = pageFontFactorOverride ?? computedFontFactor;

	// deferred to an effect: it sets state in the per-page font control and must not run mid-render
	useEffect(() => {
		reportComputedFactor(computedFontFactor);
	}, [reportComputedFactor, computedFontFactor]);

	// Mirror the main controller's display options so this isolated viewport
	// reproduces the regular MFG/overhead view exactly (hidden dimensions,
	// dimension mode, etc.). Same option list as BlueprintPanelCard.
	const mainOptions = mainContext?.controller?.options;
	const controller = useMemo(() => {
		const ctrl = new ComponentController(blueprint, {
			eventGroup: `bv-${pageIndex}-${blueprint.id}`
		});

		ctrl.navigation.displayMode = displayMode;
		ctrl.navigation.islandId = islandId;

		if (panelId !== undefined) {
			ctrl.navigation.panelId = panelId;
		}

		if (mainOptions) {
			const mirrored: Array<keyof typeof mainOptions> = [
				'showDimensions', 'showApplianceDimensions', 'showAllDimensions',
				'showDimensionsMode', 'showCutouts', 'showAppliances',
				'showApplianceLabels', 'showBreakpoints', 'showCorners',
				'showCountertops', 'showHints', 'showIslandBaseDimensions',
				'showOverheadDimensions', 'showOverlap', 'showObstructions',
				'showBacksplashes', 'showPanelInsideDimensions',
				'showPanelOutsideDimensions', 'showApplianceGroups',
				'highlightFrontPanel', 'dimensionOrientationMode',
				'applianceOpacity', 'cutoutOpacity', 'borderColor',
				'cutoutColor', 'backgroundColor', 'useFractions',
				'assetsBaseUrl', 'assetsSasToken'
			] as const;
			mirrored.forEach((key) => {
				const value = (mainOptions as unknown as Record<string, unknown>)[key];
				if (value !== undefined) {
					(ctrl.options as unknown as Record<string, unknown>)[key] = value;
				}
			});
		}

		ctrl.options.showDimensions = true;
		ctrl.options.showAppliances = true;
		ctrl.options.showBreakpoints = true;
		ctrl.options.isBlueprintView = true;
		// readonly: false — allow dimension dragging + context menu (hide dimension, etc.)
		ctrl.options.readonly = false;
		ctrl.options.contextMenu = true;
		ctrl.options.backgroundColor = '#FFFFFF';

		// Seed with current font factor; updated via effect below.
		ctrl.options.dimensionFontSizeFactor = fontFactor;
		ctrl.options.dimensions = { fontSizeFactor: fontFactor };

		// Overhead blueprint pages: hide cutouts, appliances, and their dimensions (joint-countertop excluded — it must render verbatim like the single-island countertop sheet, cutouts included)
		const isOverheadPage = pageType === 'overhead-layout'
			|| pageType === 'base-layout'
			|| pageType === 'countertop-layout';

		if (isOverheadPage) {
			ctrl.options.showCutouts = false;
			ctrl.options.showAppliances = false;
			ctrl.options.showApplianceDimensions = false;
			ctrl.options.showBreakpoints = false;
			ctrl.options.showIslandBaseDimensions = true;
			ctrl.options.showPanelInsideDimensions = true;
			// Finalized overhead pages omit wall/obstruction markers — show islands only.
			ctrl.options.showObstructions = false;

			const isNoRtaCountertop = (ctrl.kitchen?.material?.countertop?.key ?? '') === 'none';
			const isBaseLayout = pageType === 'base-layout'
				|| (pageType === 'overhead-layout' && isNoRtaCountertop);

			if (isBaseLayout) {
				ctrl.options.showCountertops = false;
				// Base layout shows inside dimensions only — no outside/overall overhead dims.
				ctrl.options.showPanelInsideDimensions = true;
				ctrl.options.showPanelOutsideDimensions = false;
				ctrl.options.showOverheadDimensions = false;
			}

			// Per-sheet override (BlueprintOverheadDimensionsToggle, GitHub #450): hide the two overall
			// width/depth dimension lines on this Overhead Layout sheet without touching the working view.
			if (hideOverheadDimensions) {
				ctrl.options.showOverheadDimensions = false;
			}
		}

		// the trim cut list IS its dimensions, so a toggled-off outside-dimension option must not blank the sheet
		if (pageType === 'flat-trim') {
			ctrl.options.showPanelOutsideDimensions = true;
		}

		return ctrl;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [blueprint, islandId, panelId, displayMode, pageIndex, pageType, hideOverheadDimensions]);

	useSheetDimensionVisibility(controller);

	if (controller.options.dimensionFontSizeFactor !== fontFactor) {
		controller.options.dimensionFontSizeFactor = fontFactor;
		controller.options.dimensions = { fontSizeFactor: fontFactor };
	}

	// Compute initial viewBox from domain model
	const initialViewBox = useMemo(() => {
		const bounds = computeModelBounds({ controller, islandId, panelId, displayMode, fontFactor });
		if (!bounds || bounds.width <= 0 || bounds.height <= 0) return '-120 -80 240 160';

		return buildViewBox(bounds);
	}, [controller, islandId, panelId, displayMode, fontFactor]);

	// Refine viewBox from actual rendered content after mount
	const measureContent = useCallback((): void => {
		const group = contentGroupRef.current;
		if (!group) return;

		try {
			// a rotated overhead sits inside this group, so getBBox here would return the box of a
			// box and frame the page far too wide -- measure what is actually drawn instead
			const rotated = controller.viewport.config.rotation % 360 !== 0
				? getRenderedContentBounds(group)
				: null;
			const bbox = rotated ?? group.getBBox();
			if (bbox.width <= 0 || bbox.height <= 0) return;

			const padX = computePadding(bbox.width);
			const padY = computePadding(bbox.height);
			const vbX = bbox.x - padX;
			const vbY = bbox.y - padY;
			const vbW = bbox.width + (padX * 2);
			const vbH = bbox.height + (padY * 2);

			setRefinedViewBox(`${vbX} ${vbY} ${vbW} ${vbH}`);
		} catch (_error) {
			// getBBox can throw if the element is not in the DOM; ignore
		}
	}, [controller]);

	useEffect(() => {
		setRefinedViewBox(null);

		return raceFrame(() => {
			measureContent();
		});
	}, [controller, measureContent]);

	// Measure SVG container pixel dimensions for ruler-matched scaling (countertop only)
	useEffect(() => {
		const el = svgRef.current;
		if (!el) return;

		const update = (): void => {
			const rect = el.getBoundingClientRect();
			if (rect.width > 0 && rect.height > 0) {
				setContainerPx({ w: rect.width, h: rect.height });
			}
		};
		update();

		const observer = new ResizeObserver(update);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		return () => { controller?.destroy(); };
	}, [controller]);

	const isOverhead = displayMode === DisplayMode.OverheadManufacturing
		|| displayMode === DisplayMode.Overhead;
	const isBacksplashMode = displayMode === DisplayMode.Backsplash
		|| displayMode === DisplayMode.BacksplashManufacturing;
	const isCornerTrimMode = displayMode === DisplayMode.CornerTrim
		|| displayMode === DisplayMode.CornerTrimManufacturing;

	// The joint countertop sheet draws its grouped tops as a scoped inline countertop row (own markers per island), not one countertop slab or a whole-kitchen overhead.
	const isJointCountertop = pageType === 'joint-countertop';

	// the trim cut list also carries no panelId, so without this it gets fitted as a countertop slab
	const isCountertopPage = !isOverhead && !isBacksplashMode && !isCornerTrimMode && !isJointCountertop && panelId === null;

	// Countertop sheets only: the overall dimension row sits further out than any
	// other ink, so the fit needs the settled measurement rather than one frame.
	const countertopInkExtent = useStabilizedInkExtent({
		groupRef: contentGroupRef,
		enabled: isCountertopPage,
		resetKey: `${islandId}|${pageIndex}|${fontFactor}`
	});

	const countertopViewBox = useMemo((): string | null => {
		if (!isCountertopPage) return null;

		const island = controller.kitchen?.design.islands.findById(islandId);
		if (!island) return null;

		const countertopWidth = island.islandSize.width;
		const rawDepth = island.islandSize.depth;
		const countertopHeight: number = typeof rawDepth === 'object' && rawDepth !== null
			? ((rawDepth as { left: number; right: number }).left
				+ (rawDepth as { left: number; right: number }).right) / 2
			: (rawDepth as number);

		// Corner-stand pentagons and rectangular slabs share the same fit path:
		// scale to the ruler against the island bounding box, then let measured
		// dimension ink shrink the scale only if it would cross the page edge.
		// The old corner-island branch used a static 1.67 padding that pinned the
		// pentagon at ~60% of the viewBox height and ignored container aspect,
		// leaving the pentagon tiny on wide pages.
		return computeFittedViewBox({
			geometry: {
				left: 0,
				right: countertopWidth,
				top: 0,
				bottom: countertopHeight
			},
			ink: countertopInkExtent,
			containerPx
		});
	}, [isCountertopPage, controller, islandId, containerPx, countertopInkExtent]);

	const viewBox = countertopViewBox ?? refinedViewBox ?? initialViewBox;

	// SKU annotations for this page — countertop sheets only
	const { activeTool, selectedAnnotationId, setSelectedAnnotationId, editingAnnotationId, setEditingAnnotationId } = useSkuAnnotation();
	const pageAnnotations = isCountertopPage
		? (controller.kitchen?.skuAnnotations?.children ?? []).filter((a) => a.pageNumber === pageIndex)
		: [];

	// Ephemeral CLEAN EDGE labels for countertop pages (render-only, not persisted)
	const cleanEdgeLabels = useMemo(() => {
		if (!isCountertopPage || !controller.kitchen) return [];
		const island = controller.kitchen.design.islands.findById(islandId);
		if (!island) return [];
		return getCleanEdgeLabels({ island, kitchen: controller.kitchen });
	}, [isCountertopPage, controller.kitchen, islandId]);

	// Sides that could take a manual CLEAN EDGE label but don't have one yet (#398) — only computed
	// while the hidden-dimensions toggle is on, since that's also the only time they're shown.
	const showCleanEdgeAddCandidates = !controller.options.readonly && (controller.options.showAllDimensions ?? false);
	const cleanEdgeAddCandidates = useMemo(() => {
		if (!isCountertopPage || !controller.kitchen || !showCleanEdgeAddCandidates) return [];
		const island = controller.kitchen.design.islands.findById(islandId);
		if (!island) return [];
		return getCleanEdgeAddableCandidates({ island, kitchen: controller.kitchen });
	}, [isCountertopPage, controller.kitchen, islandId, showCleanEdgeAddCandidates]);

	// Adjoining-island SEAM markers — blueprint countertop sheets ONLY (never app views).
	const adjoiningSeams = useMemo(() => {
		if (!isCountertopPage || !controller.kitchen) return [];
		const island = controller.kitchen.design.islands.findById(islandId);
		if (!island) return [];
		return getAdjoiningCountertopSeams({ island, kitchen: controller.kitchen });
	}, [isCountertopPage, controller.kitchen, islandId]);

	const handleSvgClick = useCallback((e: React.MouseEvent): void => {
		const target = e.target as Element;
		if (!target?.closest?.('[id]')?.id || target.tagName === 'svg') {
			setSelectedAnnotationId(null);
			setEditingAnnotationId(null);
		}
	}, [setSelectedAnnotationId, setEditingAnnotationId]);

	return (
		<AppRootContextProvider controller={controller}>
			<BlueprintSvgContextMenuWrapper svgRef={svgRef}>
				<svg
					ref={svgRef}
					xmlns="http://www.w3.org/2000/svg"
					style={{
						width: '100%',
						height: '100%',
						display: 'block'
					}}
					viewBox={viewBox}
					preserveAspectRatio="xMidYMid meet"
					onClick={pageAnnotations.length > 0 ? handleSvgClick : undefined}
				>
					<CreateBlueprintSymbols />
					<g ref={contentGroupRef}>
						{isJointCountertop
							? <RenderJointCountertopRow controller={controller} islandIds={overheadIslandIds ?? []} />
							: isOverhead
								? <DisplayOverheadMode islandIdFilter={overheadIslandIds} />
								: isBacksplashMode
									? <RenderBacksplashes />
									: isCornerTrimMode
										? <RenderCornerTrims />
										: <DisplayPanelMode />
						}
						{isOverhead && controller.kitchen && pageType !== 'joint-countertop' && (
							<OverheadIslandLetters kitchen={controller.kitchen} />
						)}
						{cleanEdgeLabels.map((label) => (
							<RenderCleanEdgeLabel key={label.id} label={label} fontSize={2.2} />
						))}
						{cleanEdgeAddCandidates.map((candidate) => (
							<RenderCleanEdgeAddCandidate key={candidate.id} islandId={islandId} candidate={candidate} fontSize={2.2} />
						))}
						{adjoiningSeams.map((marker) => (
							<RenderAdjoiningSeamMarker key={marker.id} marker={marker} fontSize={2} />
						))}
						{pageAnnotations.map((annotation) => (
							<RenderSkuAnnotation
								key={annotation.id}
								annotation={annotation}
								activeTool={activeTool}
								isSelected={annotation.id === selectedAnnotationId}
								isEditing={annotation.id === editingAnnotationId}
								fontSizeFactor={fontFactor}
								onSelectAnnotation={setSelectedAnnotationId}
								onEditAnnotation={(id) => setEditingAnnotationId(id)}
								onCloseEdit={() => setEditingAnnotationId(null)}
							/>
						))}
					</g>
				</svg>
			</BlueprintSvgContextMenuWrapper>
		</AppRootContextProvider>
	);
};

export const EmbeddedSvgViewport = React.memo(
	EmbeddedSvgViewportInner,
	(prev, next) =>
		prev.islandId === next.islandId
		&& prev.panelId === next.panelId
		&& prev.displayMode === next.displayMode
		&& prev.pageIndex === next.pageIndex
		&& prev.pageType === next.pageType
		&& (prev.overheadIslandIds ?? []).join(',') === (next.overheadIslandIds ?? []).join(',')
		&& prev.hideOverheadDimensions === next.hideOverheadDimensions
);
